import { Navigate, Outlet, useLocation } from 'react-router-dom';

import Spinner from '@components/common/Spinner';
import { useAuth } from '@context/AuthContext';

import { PATHS } from './paths';

// ---------------------------------------------------------------------------
// Loading shim — used while the initial /me bootstrap is in flight so we
// don't flash the login screen for a returning, still-logged-in user.
// ---------------------------------------------------------------------------
function AuthBootSplash() {
  return (
    <div className="grid min-h-screen place-items-center bg-page">
      <Spinner size="lg" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public-only: redirects authenticated users away from /login & /signup.
// Used so an already-signed-in user pasting /login lands on the dashboard.
// ---------------------------------------------------------------------------
export function PublicOnlyRoute() {
  const { user, loading } = useAuth();
  if (loading) return <AuthBootSplash />;
  if (user) {
    return <Navigate to={user.isOnboarded ? PATHS.dashboard : PATHS.onboarding} replace />;
  }
  return <Outlet />;
}

// ---------------------------------------------------------------------------
// Protected app surface: requires auth + completed onboarding. Unauthenticated
// users go to login (preserving the attempted URL); authenticated-but-not-
// yet-onboarded users are punted to the onboarding screen first.
// ---------------------------------------------------------------------------
export function ProtectedRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <AuthBootSplash />;
  if (!user) {
    return (
      <Navigate
        to={PATHS.login}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }
  if (!user.isOnboarded) {
    return <Navigate to={PATHS.onboarding} replace />;
  }
  return <Outlet />;
}

// ---------------------------------------------------------------------------
// Onboarding gate: only renders the onboarding page for users who actually
// need it — others are routed wherever they belong.
// ---------------------------------------------------------------------------
export function OnboardingRoute() {
  const { user, loading } = useAuth();
  if (loading) return <AuthBootSplash />;
  if (!user) return <Navigate to={PATHS.login} replace />;
  if (user.isOnboarded) return <Navigate to={PATHS.dashboard} replace />;
  return <Outlet />;
}
